/**
 * Shell config types — the operator-authored shape stored on
 * {@code OrgShell.publishedConfig}. Mirrors ThemeMaker's
 * {@code lms-portal/src/lib/platform/types.ts} {@code ShellConfig}.
 *
 * <p>The shape is FLAT — header/footer/topbar carry their config
 * inline rather than going through a {@code (style, props)} registry
 * lookup. Header styles are picked by {@link HeaderConfig#style};
 * each style is a different visual treatment of the SAME config, so
 * tenants can flip styles without reshaping their menu / brand /
 * search affordances.
 *
 * <h3>Style → component dispatch</h3>
 * Headers / footers / topbars choose their concrete component via
 * the {@code style} field on each section. The component itself lives
 * in {@code @akademi/site-builder-shells} (and tenant-private
 * packages like {@code @akademi/theme-btk} for private styles). The
 * dispatcher is in shells, not here — this package only defines the
 * shapes the dispatcher reads from.
 */

import type { LocalizedString } from './types';
import type { I18nConfig, AuthRequirement } from './i18n';
import type { MenuEntry } from './menuTypes';

// ──────────────────────────────────────────────────────────────────────────────
// Brand
// ──────────────────────────────────────────────────────────────────────────────

export interface BrandConfig {
  /** Display name shown next to / instead of the logo. */
  name?: LocalizedString;
  /** Optional tagline rendered next to the brand. */
  tagline?: LocalizedString;
  /** URL the brand link points to. Default {@code "/"}. */
  logoHref?: string;
  /** Image URL of the brand logo. */
  logo?: string;
  /** Dark-mode variant logo. Falls back to {@code logo} when unset. */
  logoDark?: string;
  /** Square / mark-only logo for collapsed sidebar rails. When set,
   *  the {@code sidebar-collapsible} header uses it as the
   *  expand-toggle affordance (a single button that doubles as brand
   *  and toggle in collapsed state). */
  logoIcon?: string;
  /** Dark-surface variant of {@link logoIcon}. */
  logoIconDark?: string;
  /** Logo image height in px inside the header's Brand mark. Leave
   *  unset to use each header style's own default (26px on the
   *  top-bar styles, 36px on sidebar styles). Width scales
   *  proportionally (the logo's natural aspect ratio is preserved). */
  logoHeight?: number;
}

// ──────────────────────────────────────────────────────────────────────────────
// Header
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Public + standard header styles. Tenant-private styles (BTK,
 * acme-boardroom, etc.) extend this via plain string values — the
 * dispatcher just looks up the string, so private styles aren't a
 * special case at the type level.
 */
export type HeaderStyle =
  | 'classic'
  | 'glass'
  | 'split'
  | 'minimal'
  | 'cinematic'
  | 'cinematic-immersive'
  | 'sidebar'
  | 'sidebar-collapsible'
  | 'none'
  // Tenant-private styles live alongside as bare strings.
  | (string & {});

/**
 * One button in the header's right-cluster action list. Operators
 * compose a small list of these via the admin Shell editor — typical
 * uses are sign-in, dashboard link, contact CTA, book-a-demo, etc.
 * Each row supports an optional icon, a visual variant, and per-row
 * auth / permission gating.
 */
export interface HeaderAction {
  label: LocalizedString;
  href: string;
  /** Icon key from the sidebar-icon catalog. Optional — label-only when omitted. */
  icon?: string;
  /** Button treatment. {@code ghost} is the default unobtrusive look. */
  variant?: 'primary' | 'secondary' | 'ghost';
  /** Open in a new tab. Defaults to same-tab. */
  target?: 'same' | 'new-tab';
  requireAuth?: AuthRequirement;
  requirePerms?: string[];
  /** User must belong to AT LEAST ONE of these dynamic groups. Empty /
   *  absent = no audience gate. Same ANY-match semantics as {@code requirePerms}. */
  requireDynamicGroupIds?: number[];
  requireSecurityGroupIds?: number[];
}

/** A single clickable row in the avatar/profile dropdown, or inside a
 *  {@link ProfileMenuGroup}'s {@code links}. Cannot itself contain a
 *  group — same one-level-only rule as {@code MenuGroup}. */
export interface ProfileMenuLink {
  kind?: 'link';
  label: LocalizedString;
  /** Tenant-relative path or absolute URL. Resolved via {@code tenantHref}. */
  href: string;
  /** Icon key from the sidebar-icon catalog. Optional — label-only when omitted. */
  icon?: string;
  requireAuth?: AuthRequirement;
  requirePerms?: string[];
  requireDynamicGroupIds?: number[];
  requireSecurityGroupIds?: number[];
}

/** A labelled cluster of {@link ProfileMenuLink} rows. Only meaningful
 *  when {@code header.profileMenuStyle} is {@code 'grid'} — the 'list'
 *  style flattens a group's links inline (the group label is dropped,
 *  same convention as top-bar {@code MenuGroup} flattening). */
export interface ProfileMenuGroup {
  kind: 'group';
  label: LocalizedString;
  /** Icon shown next to the group heading. Optional. */
  icon?: string;
  /** Tint for this group's tiles + heading dot in the grid style.
   *  Leave unset to auto-assign from the built-in rotating palette. */
  color?: string;
  links?: ProfileMenuLink[];
  requireAuth?: AuthRequirement;
  requirePerms?: string[];
  requireDynamicGroupIds?: number[];
  requireSecurityGroupIds?: number[];
}

/** One entry in the avatar/profile dropdown, authored via the admin
 *  Shell editor's {@code header.profileMenu}. Rendered between the
 *  user info card and the built-in Profile / Sign out rows.
 *
 *  <p>Existing flat rows (no {@code kind}) are plain links — adding
 *  grouping to a tenant's already-authored list is purely additive,
 *  no migration needed. */
export type ProfileMenuEntry = ProfileMenuLink | ProfileMenuGroup;

export interface HeaderConfig {
  /** Visual treatment. Picked by the operator in the admin Shell editor. */
  style?: HeaderStyle;
  /** Top-level menu — heterogeneous (links, groups, components). */
  menu?: MenuEntry[];

  /** When false, plain `kind: 'link'` menu entries stop rendering their
   *  per-entry `icon` in top-bar-style headers (classic/glass/split/
   *  minimal/cinematic/etc.). Default true — matches what sidebar-style
   *  headers already do unconditionally. Same pattern as
   *  `categoriesMenu`'s `showCategoryIcons`. */
  showMenuIcons?: boolean;

  /** Right-cluster action buttons rendered in order — sign-in /
   *  dashboard / contact / book-demo / language switcher / whatever
   *  the tenant needs. Empty / undefined hides the cluster. */
  actions?: HeaderAction[];

  /** Extra rows in the avatar/profile dropdown (Settings, Billing,
   *  Help, etc.), rendered between the user info card and the
   *  built-in Profile / Sign out rows. Empty / undefined renders just
   *  the defaults. */
  profileMenu?: ProfileMenuEntry[];

  /** Visual treatment of the profile dropdown. {@code 'list'} (default)
   *  is the classic single-column list. {@code 'grid'} renders
   *  {@code profileMenu} rows as a wide icon-tile launcher grouped by
   *  each row's {@code group} field — better suited once a tenant has
   *  many rows. */
  profileMenuStyle?: 'list' | 'grid';

  /** When true, render a search affordance in the header. */
  showSearch?: boolean;
  /** Where the search lives — defaults to {@code 'header'}. */
  searchPlacement?: 'header' | 'topbar';

  /** When true, the header search input renders centered in the empty
   *  space next to the menu (the same area a flex spacer normally
   *  occupies), instead of sitting flush next to the controls cluster.
   *  Default false. Ignored when {@code searchPlacement} is
   *  {@code 'icon'} or {@code 'hero-only'}. */
  searchCenter?: boolean;

  /** Auth-state visibility flags for the standard right-cluster
   *  affordances. {@code showSignIn} renders a sign-in/sign-out
   *  button; {@code showProfile} renders an avatar + profile menu. */
  showSignIn?: boolean;
  showProfile?: boolean;

  /** When true, render a theme-mode (light/dark) toggle. */
  showThemeToggle?: boolean;
  /** Visual style for the theme toggle — segmented control (default)
   *  or a compact dropdown select. {@code 'cycle'} is a single icon
   *  button that steps Light → Dark → System on each click, same
   *  footprint as {@code 'classic'} but with the System option. */
  themeToggleStyle?: 'segmented' | 'dropdown' | 'classic' | 'cycle';

  /** Streaming-style "floating header overlays main" mode. Only
   *  honored by header styles that handle their own visual surface
   *  (cinematic-immersive). See {@code blockMetadata.bleedsUnderHeader}
   *  for the first-block opt-in. */
  overlay?: boolean;

  /** Sidebar-style flag: collapse the topbar row above the page. */
  compact?: boolean;

  /** Width of the header's inner content row. {@code 'theme'} (default)
   *  constrains it to the same max-width as page content; {@code 'full'}
   *  stretches it edge-to-edge regardless of the theme's container.
   *  Honored by the top-bar-style headers (classic/glass/split/minimal/
   *  cinematic/cinematic-immersive); sidebar layouts ignore it. */
  maxWidth?: 'theme' | 'full';

  /** When true, the header stays pinned to the top of the viewport while
   *  the page scrolls. Default false (scrolls away with the page).
   *  Currently honored by the classic header style. */
  sticky?: boolean;

  /** Horizontal gap between top-level menu items in top-bar-style
   *  headers (classic/glass/split/minimal/cinematic/etc.). Leave
   *  unset to use each header style's own default. */
  menuGap?: 'sm' | 'md' | 'lg' | 'xl';

  /** Sidebar-collapsible: icon ids used for the collapse / expand
   *  toggle button. Defaults to {@code 'menu'} (hamburger). Authors
   *  can pick directional icons (e.g.
   *  {@code 'panel-left-close'} / {@code 'panel-left-open'}) for a
   *  clearer affordance. */
  collapseIcon?: string;
  expandIcon?: string;

  /** Placeholder text for the header search input. Operator-authored in
   *  the admin Shell editor. Applies to the default (suggest-mode) search
   *  box; when catalog search is enabled the catalog Labels placeholder
   *  ({@code search.labels.placeholder}) takes precedence. */
  searchPlaceholder?: LocalizedString;

  /** Auth gate for the WHOLE header. {@code 'in'} renders it only for
   *  signed-in users, {@code 'out'} only for anonymous visitors,
   *  {@code 'any'} (default) always. When the gate fails the shell
   *  renders headerless — same outcome as {@code style: 'none'}, and
   *  sidebar styles fall back to the plain stacked layout so no empty
   *  rail column is left behind. */
  requireAuth?: AuthRequirement;
  /** User must have AT LEAST ONE of these permissions for the header
   *  to render. Empty / absent = no permission gate. */
  requirePerms?: string[];
  /** User must belong to AT LEAST ONE of these dynamic groups for the
   *  header to render. Empty / absent = no audience gate. */
  requireDynamicGroupIds?: number[];
  requireSecurityGroupIds?: number[];

  /** Opaque per-style extras the renderer may consult. Concrete
   *  fields belong on this interface once they're stable across
   *  multiple styles. */
  [key: string]: unknown;
}

// ──────────────────────────────────────────────────────────────────────────────
// Top bar (optional announcement strip above the header)
// ──────────────────────────────────────────────────────────────────────────────

export type TopBarTone = 'neutral' | 'info' | 'warning' | 'success' | 'danger';

export interface TopBarConfig {
  /** Optional small label / kicker (e.g. {@code "NOTICE"}). */
  label?: LocalizedString;
  /** Main message body. */
  message?: LocalizedString;
  /** Optional CTA on the right side. */
  ctaHref?: string;
  ctaLabel?: LocalizedString;
  /** Visual tone token — drives colour. Default {@code 'neutral'}. */
  tone?: TopBarTone;
  /** When false (or absent), the bar is hidden — operators can
   *  toggle visibility without losing the authored copy. */
  visible?: boolean;
}

// ──────────────────────────────────────────────────────────────────────────────
// Footer
// ──────────────────────────────────────────────────────────────────────────────

export type FooterStyle = 'classic' | 'compact' | 'expanded' | (string & {});

export interface FooterLink {
  label: LocalizedString;
  href: string;
  requireAuth?: AuthRequirement;
  requirePerms?: string[];
  requireDynamicGroupIds?: number[];
  requireSecurityGroupIds?: number[];
}

export interface FooterGroup {
  label?: LocalizedString;
  links?: FooterLink[];
  requireAuth?: AuthRequirement;
  requirePerms?: string[];
  requireDynamicGroupIds?: number[];
  requireSecurityGroupIds?: number[];
}

export interface FooterSocial {
  platform: string;
  href?: string;
}

/** Mobile-app store badge. {@code platform} maps to the inline-SVG
 *  store identity used by the {@code expanded} footer style. */
export interface FooterAppBadge {
  /** Which store badge to render. Known values:
   *  {@code 'appstore'}, {@code 'googleplay'}, {@code 'appgallery'}. */
  platform: string;
  href: string;
}

/** Slim policy / legal link rendered in the footer's utility row.
 *  Used for KVKK / cookie / terms / accessibility links — anywhere
 *  one-click compliance access is expected from any page. */
export interface FooterUtilityLink {
  label: LocalizedString;
  href?: string;
  requireAuth?: AuthRequirement;
  requirePerms?: string[];
  requireDynamicGroupIds?: number[];
  requireSecurityGroupIds?: number[];
}

/** Small scrollable news/blog rail rendered in the footer — thumbnail,
 *  title and date per item, with prev/next arrows. {@code source}
 *  selects which host adapter feeds it: {@code news} reads
 *  {@link LearningSource.fetchArticles}, {@code blogs} reads
 *  {@link BlogSource.listArticles}. */
export interface FooterResourceListConfig {
  enabled?: boolean;
  title?: LocalizedString;
  source?: 'news' | 'blogs';
  limit?: number;
}

export interface FooterConfig {
  style?: FooterStyle;
  /** Width of the footer's inner content. {@code 'theme'} (default)
   *  constrains it to the same max-width as page content; {@code 'full'}
   *  stretches it edge-to-edge regardless of the theme's container. */
  maxWidth?: 'theme' | 'full';
  /** Footer surface color. Overrides each style's default background
   *  (all styles default to the same muted surface {@code expanded}
   *  has always used). CSS color string, e.g. {@code #0a0a0a}. */
  background?: string;
  /** Footer text color. Overrides each style's own default text
   *  token when set. CSS color string. */
  textColor?: string;
  groups?: FooterGroup[];
  social?: FooterSocial[];
  /** Tagline displayed in {@code expanded} style. */
  tagline?: LocalizedString;
  copyright?: LocalizedString;
  /** Slim secondary strip below the main footer — legal / KVKK /
   *  cookies. Rendered by {@code expanded} (and inline by
   *  {@code compact}) when at least one entry resolves to a label. */
  utilityLinks?: FooterUtilityLink[];
  /** Optional row of native-style app-store badges. {@code expanded}
   *  style only. */
  appBadges?: FooterAppBadge[];
  /** News/blog rail rendered above the main footer content. */
  resourceList?: FooterResourceListConfig;

  /** Auth gate for the WHOLE footer — same semantics as
   *  {@link HeaderConfig.requireAuth}. When the gate fails nothing
   *  renders below the page content. */
  requireAuth?: AuthRequirement;
  /** User must have AT LEAST ONE of these permissions for the footer
   *  to render. Empty / absent = no permission gate. */
  requirePerms?: string[];
  /** User must belong to AT LEAST ONE of these dynamic groups for the
   *  footer to render. Empty / absent = no audience gate. */
  requireDynamicGroupIds?: number[];
  requireSecurityGroupIds?: number[];

  [key: string]: unknown;
}

// ──────────────────────────────────────────────────────────────────────────────
// Theme defaults
// ──────────────────────────────────────────────────────────────────────────────

/** Corner the floating "back to top" button anchors to. */
export type ScrollToTopPosition = 'bottom-right' | 'bottom-left' | 'bottom-center';

/**
 * Floating "scroll to top" button config. Rendered at the shell root
 * on every page; the button reveals itself once the visitor has
 * scrolled past a threshold and smooth-scrolls back to the top on
 * click. Managed from the admin Shell editor's "Theme defaults" tab.
 */
export interface ScrollToTopConfig {
  /** Show the button. When false (or absent → treated as false) the
   *  widget renders nothing. Default off so existing tenants are
   *  unaffected until they opt in. */
  show?: boolean;
  /** Which corner the button anchors to. Default {@code 'bottom-right'}. */
  position?: ScrollToTopPosition;
}

/**
 * Theme-level shell defaults — how the theme mode initializes for
 * first-time visitors, plus small cross-cutting chrome affordances
 * (back-to-top button) that don't belong to header/footer.
 */
export interface ThemeDefaultsConfig {
  /** Mode to apply on first visit (before the visitor has toggled).
   *  {@code 'system'} follows OS preference. */
  defaultMode?: 'system' | 'light' | 'dark';
  /** Seeder-only flag; ignored at render time. */
  skipMenuBackfill?: boolean;
  /** Floating back-to-top button. Off unless a tenant opts in. */
  scrollToTop?: ScrollToTopConfig;
  [key: string]: unknown;
}

// ──────────────────────────────────────────────────────────────────────────────
// Help desk (floating support-ticket widget)
// ──────────────────────────────────────────────────────────────────────────────

/** One row in the help-desk widget's category picker. Picking a row
 *  prefills the ticket subject from {@code label} (except a row whose
 *  {@code value} is {@code 'other'}, which leaves the subject field
 *  editable) and advances to the ticket form. */
export interface HelpDeskCategory {
  /** Lucide icon name shown next to the label. */
  icon?: string;
  label: LocalizedString;
  /** Sent as the submitted ticket's {@code category} field. */
  value: string;
}

/**
 * Floating "help desk" widget config — a support button that opens a
 * category picker → quick ticket form, POSTed to the help center.
 * Ported from the legacy portal's {@code components/Chat/ChatBox.js};
 * distinct from the AI Chatbot ({@code ShellConfig.chatbot}), which
 * answers from a knowledge base rather than filing a ticket for a
 * human. Rendered by {@code HelpDeskWidget} from
 * {@code @akademi/site-builder-runtime}.
 */
export interface HelpDeskWidgetConfig {
  /** Show the widget. Default off. */
  show?: boolean;
  /** Who sees the button. Default {@code 'any'}. */
  requireAuth?: AuthRequirement;
  /** Which corner the button anchors to. Default {@code 'bottom-right'}. */
  position?: 'bottom-right' | 'bottom-left';
  /** Fine-tune spacing (px) from the corner, overriding the built-in
   *  default — mainly to avoid overlapping the AI Chatbot button when
   *  both sit in the same corner. */
  offsetX?: number;
  offsetY?: number;
  /** Lucide icon name for the button face. Ignored when {@code image}
   *  is set. Default {@code 'message-circle-question'}. */
  icon?: string;
  /** Full image URL overriding the icon. */
  image?: string;
  /** Hover tooltip / accessible label. Falls back to a built-in
   *  localized "Support" string. */
  tooltip?: LocalizedString;
  /** Heading inside the open panel. Falls back to a built-in
   *  localized "Support" string. */
  panelTitle?: LocalizedString;
  /** Heading above the category list. */
  menuHeading?: LocalizedString;
  /** Category rows. Empty/absent → {@code HelpDeskWidget} falls back to
   *  its own built-in default list rather than an empty picker. */
  categories?: HelpDeskCategory[];
  /** Heading above the ticket form. */
  formHeading?: LocalizedString;
  subjectPlaceholder?: LocalizedString;
  titlePlaceholder?: LocalizedString;
  messagePlaceholder?: LocalizedString;
  submitLabel?: LocalizedString;
  successMessage?: LocalizedString;
  errorMessage?: LocalizedString;
}

// ──────────────────────────────────────────────────────────────────────────────
// Overall shell
// ──────────────────────────────────────────────────────────────────────────────

/** Visual treatment of the overall page chrome.
 *
 *   {@code 'flush'}    — chrome edge-to-edge in the viewport (default)
 *   {@code 'rounded'}  — chrome wrapped in a soft "app-in-a-card" frame
 */
export type ShellFrame = 'flush' | 'rounded';

/**
 * Tenant-owned analytics / tracking provider IDs. Each field takes a
 * vendor-specific identifier — never a URL or arbitrary HTML — and
 * {@code AnalyticsScripts} constructs the vetted loader around it.
 *
 * <p>Privacy: this package does NOT ship a consent banner. Hosts
 * are responsible for their own CMP. Set {@code deferUntilConsent}
 * to suppress every script until {@code window.__lmsConsent} is
 * truthy.
 */
export interface AnalyticsIntegrationsConfig {
  /** Google Tag Manager container ID. Format: {@code GTM-XXXXXXX}. */
  gtmId?: string;
  /** Google Analytics 4 measurement ID (gtag.js direct, NOT through
   *  GTM). Format: {@code G-XXXXXXXXXX}. Running both with
   *  {@code gtmId} produces double counts. */
  ga4MeasurementId?: string;
  /** Meta (Facebook) pixel ID. Numeric, ~15 digits. */
  metaPixelId?: string;
  /** Hotjar site ID. Numeric. */
  hotjarSiteId?: string;
  /** HubSpot Hub/Portal ID. Numeric. Required by the EmbedBlock's
   *  HubSpot form/meeting variants for submission attribution. */
  hubspotHubId?: string;
  /** Plausible Analytics domain. Format: {@code example.com}. */
  plausibleDomain?: string;
  /** Welcoming Web accessibility widget ID, rendered as the loader
   *  script's {@code data-widget-id}. The script itself always loads
   *  from a fixed vendor CDN URL — see {@code AnalyticsScripts}. */
  welcomingWebId?: string;
  /** When true, every script is gated behind
   *  {@code window.__lmsConsent === true}. Default false. */
  deferUntilConsent?: boolean;
}

// ──────────────────────────────────────────────────────────────────────────────
// Search section  (top-level shell config for catalog search)
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Per-section i18n labels for the catalog search overlay.
 * Every field accepts a plain string OR a {@link LocalizedString} map
 * so operators can manage translations directly in the admin Shell editor.
 *
 * @example
 * ```json
 * "labels": {
 *   "allResults": { "tr": "Tüm Sonuçlar", "en": "All Results" },
 *   "courses":    { "tr": "Eğitimler",    "en": "Courses" }
 * }
 * ```
 */
export interface SearchSectionLabels {
  placeholder?: LocalizedString;
  allResults?: LocalizedString;
  courses?: LocalizedString;
  blog?: LocalizedString;
  news?: LocalizedString;
  faq?: LocalizedString;
  documents?: LocalizedString;
  pages?: LocalizedString;
  employees?: LocalizedString;
  seeAllCourses?: LocalizedString;
  seeAllBlog?: LocalizedString;
  seeAllNews?: LocalizedString;
  seeAllFaq?: LocalizedString;
  seeAllDocuments?: LocalizedString;
  seeAllPages?: LocalizedString;
  seeAllEmployees?: LocalizedString;
  noResults?: LocalizedString;
  typeToSearch?: LocalizedString;
  searching?: LocalizedString;
  close?: LocalizedString;
  clearSearch?: LocalizedString;
}

/**
 * Top-level shell "search" section.
 *
 * When present, {@code LiveSearchBox} switches from the suggest-dropdown
 * mode (requires a host {@code SearchSource} adapter) to a richer
 * catalog-backed overlay that shows results grouped by type in tabs.
 *
 * Managed entirely from the admin Shell editor — no code changes needed
 * to enable / configure per-section visibility, API target, or labels.
 */
/** Per-content-type section config inside {@link SearchSectionConfig}. */
export interface SearchContentSection {
  /** Show this section in search results. */
  show?: boolean;
  /** "See all" link URL for this section. */
  href?: string;
  /** "See all" link button label. Falls back to {@link SearchSectionLabels} counterpart. */
  hrefText?: LocalizedString;
}

/** One selectable entry in the header search's category filter. */
export interface SearchCategoryOption {
  /** Chip / option label. */
  label: LocalizedString;
  /** Value sent as {@code filter.category} to the catalog search API. */
  value: string;
}

export interface SearchSectionConfig {
  /** Which search the header runs.
   *
   *  {@code catalog} (the default) posts to the catalog endpoint and renders the
   *  tabbed multi-type dropdown — courses, news, FAQ and the rest. Those types
   *  are the shape of the learning catalog; a host whose content is not that
   *  shape has nothing to put in the tabs.
   *
   *  {@code suggest} renders the plain suggestion dropdown and asks the host's
   *  {@code SearchSource} for hits instead, which is what a host with its own
   *  search backend wants. A commerce storefront sets this: its products are
   *  not courses, and without it the header posts to a catalog endpoint the
   *  storefront does not serve and the dropdown 404s on every keystroke.
   *
   *  Left unset the behaviour is unchanged from before this option existed. */
  mode?: 'catalog' | 'suggest';
  /** Where Enter goes. Defaults to {@code /{tenantSlug}/search}, which suits a
   *  portal mounted under a tenant path; a host serving one tenant per domain
   *  sets it to an absolute path of its own such as {@code /search}. */
  resultsHref?: string;
  /** Numeric tenant ID used in the API path ({@code /public/{id}/catalog/search}).
   *  Optional — hosts can provide it via {@code SiteShellPresenter.tenantId} instead. */
  tenantId?: string | number;
  /** When false, hides the search affordance in the header entirely. Default true. */
  showSearch?: boolean;
  /** Placeholder text for the search input. Overrides the built-in locale default. */
  searchPlaceholder?: LocalizedString;
  /** Max results per section returned from the API. Default 3. */
  maxResults?: number;
  /** Width preset for the header search pill. Overrides the built-in
   *  default ({@code 'default'}, 220px). Ignored when the input is set to
   *  fill its parent width. See {@link SEARCH_WIDTH_PRESETS_PX}. */
  width?: 'compact' | 'default' | 'wide' | 'xwide';
  /** When true, results show as a dropdown under the header search input
   *  itself (no modal). When false (default), focusing/clicking the
   *  header search input opens the full-screen search dialog. */
  searchInline?: boolean;

  /** When true, shows a category filter (select) in the search input.
   *  Requires {@code categories} to be non-empty. Default false. */
  showCategories?: boolean;
  /** Selectable categories for the search filter. The selected value is
   *  sent as {@code filter.category} on the catalog search request. */
  categories?: SearchCategoryOption[];

  // ── Per-section config ──────────────────────────────────────────────────
  /** Courses section. Default show: true. */
  courses?: SearchContentSection;
  /** Blog posts section (API key: {@code blog}). Default show: false. */
  blog?: SearchContentSection;
  /** News section (API key: {@code news}). Default show: true. */
  news?: SearchContentSection;
  /** FAQ section. Default show: false. */
  faq?: SearchContentSection;
  /** Documents section. Default show: false. */
  documents?: SearchContentSection;
  /** Pages section. Default show: false. */
  pages?: SearchContentSection;
  /** Employees section. Default show: false. */
  employees?: SearchContentSection;

  // ── i18n labels ─────────────────────────────────────────────────────────
  /** Per-section label overrides.  Use {@link LocalizedString} maps for
   *  multi-locale tenants — the widget resolves them against the active
   *  locale read from {@code shell.i18n}. */
  labels?: SearchSectionLabels;
}

/** Pixel widths behind {@link SearchSectionConfig.width}'s presets. */
export const SEARCH_WIDTH_PRESETS_PX: Record<NonNullable<SearchSectionConfig['width']>, number> = {
  compact: 180,
  default: 220,
  wide: 280,
  xwide: 340,
};

export interface ShellConfig {
  brand?: BrandConfig;
  i18n?: I18nConfig;
  /** Theme-mode defaults + small cross-cutting chrome affordances
   *  (back-to-top button). Managed under the admin Shell editor's
   *  "Theme defaults" tab. */
  theme?: ThemeDefaultsConfig;
  header?: HeaderConfig;
  /** Note: the legacy spelling in ThemeMaker is {@code topBar} (camel-cased).
   *  Both spellings are accepted by the renderer — operator JSON that
   *  predates this rename still works. */
  topbar?: TopBarConfig;
  topBar?: TopBarConfig;
  footer?: FooterConfig;
  /** Catalog-backed search configuration.  When present, the header's
   *  {@code LiveSearchBox} switches to catalog mode (multi-type tabbed
   *  results) instead of the suggest-dropdown adapter.
   *  Managed entirely from the admin Shell editor. */
  search?: SearchSectionConfig;
  /** Sidebar-specific overrides — when present alongside a sidebar
   *  header style, fine-tunes the rail. */
  sidebar?: Record<string, unknown>;
  /** Frame wrapper around the shell. Default {@code 'flush'}. */
  frame?: ShellFrame;
  /** Tenant-owned analytics / tracking provider IDs. Consumed by
   *  {@code AnalyticsScripts}; ignored when absent. */
  integrations?: AnalyticsIntegrationsConfig;
  /** Floating help-desk support-ticket widget. Consumed by
   *  {@code HelpDeskWidget}; ignored when {@code show} is falsy. */
  helpDesk?: HelpDeskWidgetConfig;
  /** Free-form per-tenant config the host can read. */
  [key: string]: unknown;
}
